import React from 'react';
import { compose, withProps } from 'recompose';
import styled from 'styled-components';
import { withExtHrefs } from '@peloton/external-links/withExtHrefs';
import { Links } from '@peloton/links';
import { BreakpointEither } from '@peloton/responsive/helpers';
import { black, slate1 } from '@engage/colors/base';
import { spaces } from '@engage/styles/spaces';
import { AuthCopy, useSite } from '@members/copy/copy.generated';
import { Auth } from '@members/copy/models.generated/Auth';
import { Site } from '@members/copy/models.generated/Site';
import { LOGIN_ROUTE } from '@members/pg-auth/urls';
import { navSubLink, MainText, MainLink, Separator } from '../../sharedLinkStyles';
import { topNavContainerStyles } from '../containers';
import { PelotonLogo } from '../icons/PelotonLogo';
import { User as UserIcon } from '../icons/User';
import Link from '../NavLink';

export const GuestNavView: React.FC<React.PropsWithChildren<Props>> = props => (
  <BreakpointEither breakpoint="tablet">
    <MobileGuestNavView {...props} />
    <StandardGuestNavView {...props} />
  </BreakpointEither>
);

const StandardGuestNavView: React.FC<React.PropsWithChildren<Props>> = props => (
  <Container>
    <HomeLink />
    <MainLinks>
      <MainLink href={props.pelotonHref} isExternal>
        <MainText>Peloton</MainText>
      </MainLink>
      <Separator />
      <MainLink href={props.loginHref} isExternal data-test-id="loginLink" target="_self">
        <Icon />
        <MainText>
          <AuthCopy id={Auth.GuestNavCta} />
        </MainText>
      </MainLink>
    </MainLinks>
  </Container>
);

const MobileGuestNavView: React.FC<React.PropsWithChildren<Props>> = props => (
  <MobileContainer href={props.loginHref} data-test-id="loginLink">
    <MobileNavPelotonLogo />
    <MobileUserContainer>
      <MobileIcon />
      <MobileText>
        <AuthCopy id={Auth.GuestNavCta} />
      </MobileText>
    </MobileUserContainer>
  </MobileContainer>
);

type Props = {
  pelotonHref: string;
  loginHref: string;
};

const TopNavGuest = compose(
  withExtHrefs({ pelotonHref: Links.peloton }),
  withProps((props: Props) => ({
    ...props,
    loginHref: LOGIN_ROUTE,
  })),
)(GuestNavView);

const HomeLink = () => (
  <Link aria-label={useSite(Site.HomeAriaLabel)} href="/">
    <NavPelotonLogo />
  </Link>
);

const Container = styled.div`
  ${topNavContainerStyles}
`;

const MainLinks = styled.div`
  display: flex;
  align-items: center;
`;

// Mobile size styles

const Icon = styled(UserIcon)`
  width: 16px;
  height: 16px;
  fill: ${slate1};
  margin-right: ${spaces.xSmall}px;
`;

const NavPelotonLogo = styled(PelotonLogo)`
  fill: ${black};
  height: 28px;
  width: 20px;
`;

const MobileContainer = styled.a`
  ${topNavContainerStyles}
`;

const MobileUserContainer = styled.div`
  display: flex;
  align-items: center;
`;

const MobileText = styled.span`
  ${navSubLink}
  color: ${slate1};
`;

const MobileIcon = styled(UserIcon)`
  width: 14px;
  height: 14px;
  fill: ${slate1};
  margin-right: ${spaces.xxSmall}px;
`;

const MobileNavPelotonLogo = styled(PelotonLogo)`
  fill: ${black};
  height: 23px;
  width: 16px;
`;

export default TopNavGuest;
